import { WintSuperkollAPIModel } from "wint-api-client/dist/types";

export interface VatReportsAvailabilityOutcome {
  available: boolean;
  /** The question could not be answered, which is not the same as a no. Never cached. */
  failed: boolean;
}

export const fromVatReportsAvailability = (
  result?: WintSuperkollAPIModel.VatReportsAvailability
): VatReportsAvailabilityOutcome => ({ available: !!result?.available, failed: false });

// 403 (not an advisor) and 404 (a backend without the endpoint yet) are answers, not failures. Anything else — a
// network blip, a 500, a cancelled request — says nothing about the company, so it must not read as a no.
export const fromVatReportsAvailabilityError = (error: unknown): VatReportsAvailabilityOutcome => {
  const status = (error as { status?: unknown } | undefined)?.status;
  return { available: false, failed: status !== 403 && status !== 404 };
};

export const getVatReportsAvailabilityCacheKey = (personId: number | undefined, companyId: number) =>
  `${personId ?? ""}:${companyId}`;

// Kept per user and company for the session, so the shortcut list and both pages ask once rather
// than on every mount.
const resolvedChecks = new Map<string, boolean>();
const pendingChecks = new Map<string, Promise<VatReportsAvailabilityOutcome>>();

export const getCachedVatReportsAvailability = (key: string): boolean | undefined => resolvedChecks.get(key);

/** Asks once per key: answers are kept, failures are not, and concurrent callers share one request. */
export const checkVatReportsAvailability = (
  key: string,
  getAvailability: () => Promise<WintSuperkollAPIModel.VatReportsAvailability | undefined>
): Promise<VatReportsAvailabilityOutcome> => {
  const resolved = resolvedChecks.get(key);
  if (resolved !== undefined) return Promise.resolve({ available: resolved, failed: false });

  const pending = pendingChecks.get(key);
  if (pending) return pending;

  const request = getAvailability()
    .then(fromVatReportsAvailability, fromVatReportsAvailabilityError)
    .then((outcome) => {
      if (!outcome.failed) resolvedChecks.set(key, outcome.available);
      return outcome;
    })
    .finally(() => pendingChecks.delete(key));

  pendingChecks.set(key, request);
  return request;
};

export const resetVatReportsAvailabilityCache = () => {
  resolvedChecks.clear();
  pendingChecks.clear();
};

export type VatReportsAccess = "checking" | "failed" | "denied" | "granted";

/** What a VAT report page may do: wait, offer a retry, show the no-permission page, or load. */
export const getVatReportsAccess = (
  isAdvisor: boolean,
  check: { available: boolean; failed: boolean; loading: boolean }
): VatReportsAccess => {
  if (!isAdvisor) return "denied";
  if (check.loading) return "checking";
  if (check.failed) return "failed";
  return check.available ? "granted" : "denied";
};
